/**
 * Foundational Button Component
 * Apple-inspired styling with Nothing Technology's geometric precision
 * Supports haptic feedback, accessibility, and multiple variants
 */

import React from 'react';
import {
  Pressable,
  Text,
  ActivityIndicator,
  View,
  StyleSheet,
  ViewStyle,
  TextStyle,
  AccessibilityRole,
} from 'react-native';
import { useTheme } from '../../themes/ThemeProvider';
import { useHaptics } from '../../hooks/useHaptics';
import { colorTokens } from '../../tokens/colors';
import { spacingTokens } from '../../tokens/spacing';
import { typographyTokens } from '../../tokens/typography';
import { motionTokens } from '../../tokens/motion';

export interface ButtonProps {
  /**
   * Button variant determining visual style
   */
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'ghost'
    | 'destructive'
    | 'outline';

  /**
   * Button size affecting dimensions and typography
   */
  size?: 'sm' | 'md' | 'lg' | 'base';

  /**
   * Button content
   */
  children: React.ReactNode;

  /**
   * Press handler
   */
  onPress: () => void;

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * Loading state with spinner
   */
  loading?: boolean;

  /**
   * Optional icon element
   */
  icon?: React.ReactNode;

  /**
   * Full width button
   */
  fullWidth?: boolean;

  /**
   * Accessibility label
   */
  accessibilityLabel?: string;

  /**
   * Accessibility hint
   */
  accessibilityHint?: string;

  /**
   * Test identifier
   */
  testID?: string;

  /**
   * Custom style override
   */
  style?: ViewStyle;

  /**
   * Custom text style override
   */
  textStyle?: TextStyle;
}

/**
 * Button Component with Apple-inspired design and Nothing's geometric precision
 */
export function Button({
  variant = 'primary',
  size = 'md',
  children,
  onPress,
  disabled = false,
  loading = false,
  icon,
  fullWidth = false,
  accessibilityLabel,
  accessibilityHint,
  testID,
  style,
  textStyle,
}: ButtonProps) {
  const { theme } = useTheme();
  const haptics = useHaptics();

  // Handle press with haptic feedback
  const handlePress = () => {
    if (!disabled && !loading) {
      // Provide haptic feedback based on variant
      const hapticType = variant === 'primary' ? 'medium' : 'light';
      haptics.impact(hapticType);
      onPress();
    }
  };

  // Get button styles
  const buttonStyles = getButtonStyles({
    variant,
    size,
    disabled,
    loading,
    fullWidth,
    theme,
  });

  // Get text styles
  const buttonTextStyles = getTextStyles({
    variant,
    size,
    disabled,
    theme,
  });

  return (
    <Pressable
      style={({ pressed }) => [
        buttonStyles.container,
        pressed && !disabled && !loading && buttonStyles.pressed,
        style,
      ]}
      onPress={handlePress}
      disabled={disabled || loading}
      accessibilityRole='button'
      accessibilityLabel={
        accessibilityLabel ||
        (typeof children === 'string' ? children : undefined)
      }
      accessibilityHint={accessibilityHint}
      accessibilityState={{
        disabled: disabled || loading,
        busy: loading,
      }}
      testID={testID && testID.trim() ? testID : undefined}
    >
      <View style={buttonStyles.content}>
        {loading && (
          <ActivityIndicator
            size='small'
            color={buttonTextStyles.color}
            style={buttonStyles.spinner}
            testID={testID && testID.trim() ? `${testID}-spinner` : undefined}
          />
        )}
        {icon && !loading && (
          <View
            style={buttonStyles.icon}
            testID={testID && testID.trim() ? `${testID}-icon` : undefined}
          >
            {icon}
          </View>
        )}
        <Text
          style={[buttonTextStyles, textStyle]}
          numberOfLines={1}
          testID={testID && testID.trim() ? `${testID}-text` : undefined}
        >
          {children}
        </Text>
      </View>
    </Pressable>
  );
}

/**
 * Get button container and layout styles
 */
function getButtonStyles({
  variant,
  size,
  disabled,
  loading,
  fullWidth,
  theme,
}: {
  variant: ButtonProps['variant'];
  size: ButtonProps['size'];
  disabled: boolean;
  loading: boolean;
  fullWidth: boolean;
  theme: any;
}) {
  const colors = theme.colors;
  const spacing = theme.spacing;
  const elevation = theme.spacing.elevation;

  // Size configurations
  const sizeConfig = {
    sm: { height: 32, paddingHorizontal: 12, borderRadius: 6 },
    base: { height: 40, paddingHorizontal: 16, borderRadius: 8 },
    md: { height: 40, paddingHorizontal: 16, borderRadius: 8 },
    lg: { height: 48, paddingHorizontal: 20, borderRadius: 10 },
  };

  const currentSize = sizeConfig[size || 'md'];

  // Variant configurations
  const variantConfig = {
    primary: {
      backgroundColor: disabled ? colors.neutral[300] : colors.primary[500],
      borderWidth: 0,
      borderColor: 'transparent',
      ...elevation[2],
    },
    secondary: {
      backgroundColor: disabled
        ? colors.neutral[100]
        : colors.surface.secondary,
      borderWidth: 1,
      borderColor: disabled ? colors.neutral[200] : colors.border.primary,
      ...elevation[1],
    },
    tertiary: {
      backgroundColor: disabled ? colors.neutral[50] : colors.surface.primary,
      borderWidth: 1,
      borderColor: disabled ? colors.neutral[200] : colors.primary[200],
      ...elevation[0],
    },
    ghost: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderColor: 'transparent',
      ...elevation[0],
    },
    outline: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: disabled ? colors.neutral[200] : colors.primary[500],
      ...elevation[0],
    },
    destructive: {
      backgroundColor: disabled
        ? colors.neutral[300]
        : colors.semantic.error.DEFAULT,
      borderWidth: 0,
      borderColor: 'transparent',
      ...elevation[2],
    },
  };

  const currentVariant = variantConfig[variant || 'primary'];

  return StyleSheet.create({
    container: {
      height: currentSize.height,
      paddingHorizontal: currentSize.paddingHorizontal,
      borderRadius: currentSize.borderRadius,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      width: fullWidth ? '100%' : undefined,
      minWidth: currentSize.height, // Ensure square minimum for icon-only buttons
      opacity: disabled || loading ? 0.6 : 1,
      ...currentVariant,
    },
    pressed: {
      transform: [{ scale: 0.98 }],
      opacity: 0.8,
    },
    content: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    spinner: {
      marginRight: spacing.spacing[2],
    },
    icon: {
      marginRight: spacing.spacing[2],
    },
  });
}

/**
 * Get button text styles
 */
function getTextStyles({
  variant,
  size,
  disabled,
  theme,
}: {
  variant: ButtonProps['variant'];
  size: ButtonProps['size'];
  disabled: boolean;
  theme: any;
}) {
  const colors = theme.colors;
  const typography = theme.typography;

  // Size configurations
  const sizeConfig = {
    sm: { fontSize: 14, lineHeight: 20 },
    base: { fontSize: 16, lineHeight: 24 },
    md: { fontSize: 16, lineHeight: 24 },
    lg: { fontSize: 18, lineHeight: 28 },
  };

  const textSizeConfig = {
    sm: { fontSize: 14, lineHeight: 20 },
    base: { fontSize: 16, lineHeight: 24 },
    md: { fontSize: 16, lineHeight: 24 },
    lg: { fontSize: 18, lineHeight: 28 },
  };

  const currentSize = sizeConfig[size || 'md'];

  // Variant text color configurations
  const variantConfig = {
    primary: {
      color: disabled ? colors.neutral[500] : colors.text.inverse,
    },
    secondary: {
      color: disabled ? colors.neutral[400] : colors.text.primary,
    },
    tertiary: {
      color: disabled ? colors.neutral[400] : colors.primary[600],
    },
    ghost: {
      color: disabled ? colors.neutral[400] : colors.text.primary,
    },
    outline: {
      color: disabled ? colors.neutral[400] : colors.primary[600],
    },
    destructive: {
      color: disabled ? colors.neutral[500] : colors.text.inverse,
    },
  };

  const currentVariant = variantConfig[variant || 'primary'];

  return {
    fontFamily: typography.fontFamily.sans.join(', '),
    fontSize: currentSize.fontSize,
    lineHeight: currentSize.lineHeight,
    fontWeight: typography.fontWeight.semibold,
    letterSpacing: typography.variants.button.letterSpacing,
    textAlign: 'center' as const,
    ...currentVariant,
  };
}

export default Button;
